import type { PriceId } from '@iheartjane/types';

export interface WeightSpecial {
  discount_amount?: string | null;
  discount_percent?: string | null;
  discount_price?: string | null;
  discount_type?: string | null;
  price?: number;
  special_custom_badge?: string | null;
  special_id?: number;
}

type WeightSpecialFields = {
  [K in PriceId as `special_price_${K}`]?: WeightSpecial | null;
};

type SpecialTagProduct = WeightSpecialFields & {
  applicable_special_types?: string[] | null;
  special_amount?: string | null;
  special_custom_badge?: string | null;
  special_id?: number | null;
};

interface SpecialTagStore {
  store_compliance_language_settings?: {
    bundle?: string | null;
    specials_badge?: string | null;
  } | null;
}

const WEIGHT_LABELS: { [key in PriceId]: string } = {
  each: 'Each',
  half_gram: '1/2 G',
  gram: '1 G',
  two_gram: '2 G',
  eighth_ounce: '1/8 OZ',
  quarter_ounce: '1/4 OZ',
  half_ounce: '1/2 OZ',
  ounce: '1 OZ',
};

const formatDiscountAmount = (discount: WeightSpecial, priceId: PriceId) => {
  const {
    discount_type,
    discount_amount,
    discount_percent,
    discount_price,
    price,
  } = discount;

  const formatPrice = (price = '0') => {
    const floatPrice = parseFloat(price);
    return floatPrice % 1 === 0 ? floatPrice.toFixed(0) : floatPrice.toFixed(2);
  };

  switch (discount_type) {
    case 'percent':
      return discount_percent && parseFloat(discount_percent) !== 0
        ? `${parseFloat(discount_percent).toString()}% OFF ${WEIGHT_LABELS[priceId]}`
        : 'SALE';
    case 'dollar_amount':
      return discount_amount && parseFloat(discount_amount) !== 0
        ? `$${formatPrice(discount_amount)} OFF ${WEIGHT_LABELS[priceId]}`
        : 'SALE';
    case 'target_price':
      return discount_price && parseFloat(discount_price) !== price
        ? `$${formatPrice(discount_price)} ${WEIGHT_LABELS[priceId]}`
        : 'SALE';
    default:
      return 'SALE';
  }
};

interface GetSpecialTagLabelArgs {
  defaultWeight?: PriceId | null;
  product: SpecialTagProduct;
  shortenLabel?: boolean;
  store?: SpecialTagStore;
}

/**
 * Priority for badge:
 * 1. Weight-level custom badge
 * 2. Product-level custom badge
 * 3. Weight-specific discount amount (when different from product special)
 * 4. Product special amount
 * 5. Bundle compliance label or 'Bundle'
 */
const resolveSpecialLabel = ({
  defaultWeight,
  product,
  store,
}: Pick<GetSpecialTagLabelArgs, 'defaultWeight' | 'product' | 'store'>) => {
  const defaultWeightSpecial =
    defaultWeight && product[`special_price_${defaultWeight}`];

  if (defaultWeightSpecial?.special_custom_badge) {
    return defaultWeightSpecial.special_custom_badge;
  }

  if (product?.special_custom_badge) {
    return product.special_custom_badge;
  }

  if (defaultWeightSpecial && defaultWeight) {
    const weightSpecialMatchesProductSpecial =
      defaultWeightSpecial.special_id === product.special_id;

    if (!weightSpecialMatchesProductSpecial) {
      return formatDiscountAmount(defaultWeightSpecial, defaultWeight);
    }
  }

  if (product?.special_amount) {
    return product.special_amount;
  }

  if (product?.applicable_special_types?.includes('bundle')) {
    return store?.store_compliance_language_settings?.['bundle'] || 'Bundle';
  }

  return undefined;
};

export const getSpecialTagLabel = ({
  defaultWeight,
  product,
  shortenLabel,
  store,
}: GetSpecialTagLabelArgs) => {
  const resolvedLabel = resolveSpecialLabel({ defaultWeight, product, store });

  const specialLabel =
    resolvedLabel && resolvedLabel !== 'sale' ? resolvedLabel : 'SALE';

  const complianceLabel =
    store?.store_compliance_language_settings?.['specials_badge'];

  const useComplianceLanguage =
    specialLabel === 'SALE' ||
    (shortenLabel && specialLabel.split(' ').length > 2);

  const labelToRender =
    complianceLabel && useComplianceLanguage ? complianceLabel : specialLabel;

  return shortenLabel && labelToRender.length > 7
    ? `${labelToRender.substring(0, 7)}...`
    : labelToRender;
};
